<template>
  <p-dialog
    :visible.sync="visible"
    header="Edit Credit Card"
    :closable="true"
    :modal="true"
    @hide="onHide"
  >
    <p-inlinemessage v-if="errorMsg" severity="error" class="p-mb-3 style-msg">
      {{ errorMsg }}
    </p-inlinemessage>

    <div class="p-fluid" v-if="card">
      <div class="card-summary p-mb-4 p-p-3" style="background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%); border-radius: 10px; color: #ffffff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
        <div style="display: flex; align-items: center; gap: 12px;">
          <i class="pi pi-credit-card" style="font-size: 1.3rem; color: #d4af37;"></i>
          <div>
            <div style="text-transform: capitalize; font-weight: 600; font-size: 0.95rem; letter-spacing: 0.5px;">{{ card.brand }}</div>
            <div style="color: rgba(255, 255, 255, 0.6); font-size: 0.8rem; font-family: monospace;">•••• •••• •••• {{ card.last4 }}</div>
          </div>
        </div>
        <div v-if="card.isDefault" style="background: linear-gradient(135deg, #d4af37 0%, #aa7c11 100%); color: #000000; font-size: 0.55rem; font-weight: 800; padding: 2px 6px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Default</div>
      </div>

      <div class="p-field p-mb-3">
        <label for="editCardholderName" class="p-mb-1" style="display: block;">Cardholder Name</label>
        <p-inputText
          id="editCardholderName"
          v-model="cardholderName"
          placeholder="Cardholder Name"
        />
      </div>

      <div class="p-grid p-mb-2">
        <div class="p-col-6">
          <div class="p-field">
            <label for="expMonth" class="p-mb-1" style="display: block;">Expiry Month</label>
            <p-dropdown
              id="expMonth"
              v-model="expMonth"
              :options="monthOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Month"
            />
          </div>
        </div>
        <div class="p-col-6">
          <div class="p-field">
            <label for="expYear" class="p-mb-1" style="display: block;">Expiry Year</label>
            <p-dropdown
              id="expYear"
              v-model="expYear"
              :options="yearOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Year"
            />
          </div>
        </div>
      </div>
      <small style="color: #6c757d;">Note: Stripe allows updating expiration date and cardholder name. Card numbers cannot be modified directly.</small>
    </div>

    <template #footer>
      <p-button
        label="Save Changes"
        icon="pi pi-check"
        :loading="loading"
        :disabled="loading"
        @click="handleUpdateCard"
      />
      <p-button
        label="Cancel"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loading"
        @click="onHide"
      />
    </template>
  </p-dialog>
</template>

<script>
import { updateSavedCard } from '../../../services/user'

export default {
  name: 'EditCardModal',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    card: {
      type: Object,
      default: null,
    },
  },
  data() {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = 0; i < 20; i++) {
      years.push({ label: String(currentYear + i), value: currentYear + i })
    }
    const months = Array.from({ length: 12 }, (_, i) => {
      const monthNum = i + 1
      const formatted = monthNum < 10 ? `0${monthNum}` : `${monthNum}`
      return { label: `${formatted} - ${this.getMonthName(i)}`, value: monthNum }
    })

    return {
      cardholderName: '',
      expMonth: null,
      expYear: null,
      monthOptions: months,
      yearOptions: years,
      loading: false,
      errorMsg: '',
    }
  },
  watch: {
    visible(val) {
      if (val && this.card) {
        this.errorMsg = ''
        this.cardholderName = this.card.cardholderName || ''
        this.expMonth = this.card.expMonth || null
        this.expYear = this.card.expYear || null
      }
    },
  },
  methods: {
    getMonthName(index) {
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return names[index]
    },
    async handleUpdateCard() {
      if (!this.card || !this.card.id) return
      this.errorMsg = ''
      this.loading = true
      try {
        const payload = {
          cardholderName: this.cardholderName,
          expMonth: Number(this.expMonth),
          expYear: Number(this.expYear),
        }
        const res = await updateSavedCard(this.card.id, payload)
        if (res && res.success) {
          this.$emit('cardUpdated')
          this.onHide()
        } else {
          this.errorMsg = res?.message || 'Failed to update card'
        }
      } catch (err) {
        console.error('Error updating card:', err)
        this.errorMsg = 'An error occurred while updating card'
      } finally {
        this.loading = false
      }
    },
    onHide() {
      this.$emit('update:visible', false)
      this.$emit('toggleModal', false)
    },
  },
}
</script>

<style scoped>
.style-msg {
  width: 100%;
}
</style>
